import { AbstractControl, ReactiveFormsModule } from '@angular/forms';

import { Component, OnInit, AfterViewInit, AfterViewChecked, ChangeDetectionStrategy, ElementRef, ChangeDetectorRef, NgZone, SimpleChanges, OnChanges, input, output, inject, viewChild } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { LocationService } from '@fitness-force/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { Store } from '@ngrx/store';
import { environment } from '@fitness-force/environments';
import { LOCATION_STATE } from '@fitness-force/state';

@Component({
  selector: 'ff-google-places',
  templateUrl: './google-places.component.html',
  styleUrls: ['./google-places.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    InputTextModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    StyleClassModule,
    ButtonModule
]
})
export class GooglePlacesComponent implements OnInit, OnChanges, AfterViewInit, AfterViewChecked {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private locationService = inject(LocationService);
  private store = inject(Store);

  readonly searchElementRef = viewChild<ElementRef>('search');
  readonly mapElementRef = viewChild<ElementRef>('map');
  readonly fetchPlaceInfo = output<any>();
  readonly competitorName = input<AbstractControl | any>();
  readonly placeId = input<string | any>();
  readonly placeHolderName = input<string | any>();
  readonly showMap = input<boolean>(false);
  readonly placeDetails = input<any>(null);
  currentPlaceDetails: any = null;
  isPlaceSearched = false;
  private isMapInitialized = false;
  private mapInstance: any = null; // Store map instance for updates

  isLocationSelected = false;
  isDetectingLocation = false;
  filteredPlaces: any[] | undefined;

  ngOnInit(): void {
    // Listen for Google Maps authentication failures
    (window as any).gm_authFailure = () => {
      console.error('Google Maps authentication failed - domain not authorized. Add domain to API key restrictions in Google Cloud Console:', window.location.origin);
      this.handleComponentError('Google Maps authentication failed - domain not authorized');
    };
  }

  ngAfterViewInit() {
    try {
      // Ensure input field is functional
      setTimeout(() => {
        this.ensureInputFieldFunctionality();
      }, 100);

      // Fallback timeout for input functionality
      setTimeout(() => {
        if (!this.showMap()) {
          this.ensureInputFieldFunctionality();
        }
      }, 3000);

      this.loadGoogleMapsAPI()
        .then(() => {
          if (!this.isGoogleMapsAvailable()) {
            this.handleComponentError('Google Maps API not available after loading');
            return;
          }

          const placeId = this.placeId();
          if (placeId) {
            this.getPlaceDetailsById(placeId)
              .then((placeResult) => {
                const formattedAddress = placeResult.formattedAddress || placeResult.formatted_address;
                const competitorName = this.competitorName();
                if (competitorName && competitorName.setValue) {
                  competitorName.setValue(formattedAddress);
                }
                this.currentPlaceDetails = placeResult;
                if (this.showMap()) {
                  this.initGoogleMaps();
                }
              })
              .catch((error) => {
                console.error('Error fetching place details:', error);
                if (this.showMap()) {
                  this.initGoogleMaps();
                }
              });
          }

          if (!this.showMap()) {
            this.initAutocomplete();
          } else {
            this.initGoogleMaps();
          }
        })
        .catch((error) => {
          console.error('Failed to load Google Maps API:', error);
          this.handleComponentError('Failed to load Google Maps API');
        });
    } catch (error) {
      console.error('Unexpected error in Google Places component:', error);
      this.handleComponentError('Unexpected error in component initialization');
    }
  }

  /**
   * Ensure the input field is always functional regardless of Google Maps API status
   */
  private ensureInputFieldFunctionality(): void {
    const searchElementRef = this.searchElementRef();
    if (!this.showMap() && searchElementRef?.nativeElement) {
      const autocompleteElement = searchElementRef.nativeElement;
      autocompleteElement.disabled = false;
    }
  }

  private handleComponentError(errorMessage: string): void {
    console.error('Google Places Component Error:', errorMessage);
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['placeDetails']) {
      const currentValue = changes['placeDetails'].currentValue;
      const previousValue = changes['placeDetails'].previousValue;

      if (currentValue && currentValue !== previousValue) {
        if (this.showMap() && this.mapElementRef()?.nativeElement) {
          this.isMapInitialized = false;
          this.initGoogleMaps();
          this.isMapInitialized = true;
        }
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.showMap() && this.placeDetails() && this.mapElementRef()?.nativeElement && !this.isMapInitialized) {
      this.initGoogleMaps();
      this.isMapInitialized = true;
    }
  }

  loadGoogleMapsAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isScriptLoaded() && this.isGoogleMapsAvailable()) {
        resolve();
        return;
      }

      if (this.isScriptLoaded()) {
        this.waitForGoogleMaps(resolve, reject);
        return;
      }

      const script = document.createElement('script');

      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.GOOGLE_PLACES}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.id = 'google-maps-api';


      document.head.appendChild(script);

      script.onload = () => {
      };

      script.onerror = (error) => {
        console.error('Failed to load Google Maps script');
        reject(new Error('Failed to load Google Maps API script'));
      };
    });
  }

  private waitForGoogleMaps(resolve: () => void, reject: (error: any) => void, attempts: number = 0): void {
    const maxAttempts = 50;
    const interval = 100;

    if (this.isGoogleMapsAvailable()) {
      resolve();
      return;
    }

    if (attempts >= maxAttempts) {
      console.error('Google Maps Places library not available after waiting');
      reject(new Error('Google Maps Places library not available. Check API key and billing status.'));
      return;
    }

    setTimeout(() => {
      this.waitForGoogleMaps(resolve, reject, attempts + 1);
    }, interval);
  }

  private isGoogleMapsAvailable(): boolean {
    return typeof google !== 'undefined' &&
           !!google.maps &&
           !!google.maps.places &&
           !!google.maps.places.PlacesService &&
           !!(google.maps as any).marker &&
           !!(google.maps as any).marker.AdvancedMarkerElement;
  }

  /**
   * Get user location with multiple fallback strategies
   */
  private getUserLocation(): Promise<{ lat: number, lng: number }> {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            resolve(location);
          },
          (error) => {
            this.tryIpGeolocation(resolve, reject);
          },
          {
            timeout: 3000,
            enableHighAccuracy: false,
            maximumAge: 300000
          }
        );
      } else {
        this.tryIpGeolocation(resolve, reject);
      }
    });
  }

  /**
   * Fallback to IP-based geolocation with error handling
   */
  private tryIpGeolocation(resolve: (location: { lat: number, lng: number }) => void, reject: (error: Error) => void): void {
    this.locationService.getIpData().subscribe({
      next: (ipData) => {
        try {
          if (ipData && ipData.loc) {
            const [lat, lng] = ipData.loc.split(',').map(Number);
            if (!isNaN(lat) && !isNaN(lng)) {
              const location = { lat, lng };
              resolve(location);
              return;
            }
          }
          throw new Error('Invalid location data from IP service');
        } catch (error) {
          reject(new Error('Failed to parse IP geolocation data'));
        }
      },
      error: (error) => {
        if (error.status === 429) {
          reject(new Error('IP geolocation service rate limited'));
        } else if (error.status === 0) {
          reject(new Error('IP geolocation service unreachable'));
        } else {
          reject(new Error(`IP geolocation service error: ${error.message || error.status || 'Unknown error'}`));
        }
      }
    });
  }

  /**
   * Get place details by place ID
   * Uses google.maps.places.Place API (recommended) with fallback to PlacesService
   */
  private getPlaceDetailsById(placeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // Try new Place API first (recommended)
      if ((google.maps.places as any).Place) {
        console.log('🔄 Using new Place API for place details');
        this.getPlaceDetailsWithNewAPI(placeId)
          .then(resolve)
          .catch((error) => {
            console.warn('⚠️ New Place API failed, falling back to PlacesService (deprecated but functional):', error.message);
            // Fallback to legacy API if new API fails
            // this.getPlaceDetailsWithLegacyAPI(placeId, resolve, reject);
          });
      } else {
        // Use legacy PlacesService as fallback
        console.warn('⚠️ New Place API not available, using legacy PlacesService');
        // this.getPlaceDetailsWithLegacyAPI(placeId, resolve, reject);
      }
    });
  }

  private async getPlaceDetailsWithNewAPI(placeId: string): Promise<any> {
    // This will be used when the new Place API becomes available
    const PlaceConstructor = (google.maps.places as any).Place;
    const place = new PlaceConstructor({
      id: placeId,
    });

    await place.fetchFields({
      fields: [
        'id',
        'displayName',
        'formattedAddress',
        'location'
      ]
    });

    return {
      place_id: place.id,
      formatted_address: place.formattedAddress,
      name: place.displayName,
      geometry: {
        location: place.location
      }
    };
  }

  private getPlaceDetailsWithLegacyAPI(placeId: string, resolve: (value: any) => void, reject: (reason?: any) => void): void {
    // Use appropriate element for PlacesService - create a temporary div if needed
    let serviceElement = this.showMap() ? this.mapElementRef()?.nativeElement : this.searchElementRef()?.nativeElement;
    
    if (!serviceElement) {
      // Create temporary element for PlacesService if no suitable element exists
      serviceElement = document.createElement('div');
    }

    const placesService = new google.maps.places.PlacesService(serviceElement);
    
    placesService.getDetails(
      {
        placeId: placeId,
        fields: [
          'place_id',
          'formatted_address', 
          'name',
          'geometry'
        ]
      },
      (placeResult, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && placeResult) {
          resolve(placeResult);
        } else {
          reject(new Error(`Places service failed with status: ${status}`));
        }
      }
    );
  }

  initAutocomplete(): void {
    if (!this.searchElementRef()?.nativeElement) {
      console.error('Search element not available for autocomplete');
      return;
    }

    // The gmp-place-autocomplete element handles initialization automatically
    // Place selection is handled via template binding: (gmp-select)="onPlaceSelect($event)"
  }



  async initGoogleMaps() {
    const mapElementRef = this.mapElementRef();
    if (!mapElementRef?.nativeElement) {
      console.error('Map element not available for Google Maps initialization');
      return;
    }

    if (this.placeDetails != null) {
      const placeDetails = this.placeDetails();
      const lat = typeof placeDetails.geometry.location.lat === 'function'
        ? placeDetails.geometry.location.lat()
        : placeDetails.geometry.location.lat;
      const placeDetailsValue = this.placeDetails();
      const lng = typeof placeDetailsValue.geometry.location.lng === 'function'
        ? placeDetailsValue.geometry.location.lng()
        : placeDetailsValue.geometry.location.lng;

      const position = { lat: Number(lat), lng: Number(lng) };

      if (this.mapInstance) {
        this.mapInstance.setCenter(position);
        this.mapInstance.setZoom(15);

        const placeDetailsVal = this.placeDetails();
        new (google.maps as any).marker.AdvancedMarkerElement({
          map: this.mapInstance,
          position: position,
          title: placeDetailsVal.name || placeDetailsVal.formatted_address || 'Selected Location',
        });
      } else {
        this.mapInstance = new google.maps.Map(mapElementRef.nativeElement, {
          center: position,
          zoom: 15,
          mapId: environment.GOOGLE_MAPS_MAP_ID,
        } as any);

        const placeDetailsVal = this.placeDetails();
        new (google.maps as any).marker.AdvancedMarkerElement({
          map: this.mapInstance,
          position: position,
          title: placeDetailsVal.name || placeDetailsVal.formatted_address || 'Selected Location',
        });
      }
    } else {
      // Try to get user's location for default map center
      let defaultLocation = { lat: 25.2048, lng: 55.2708 }; // Dubai fallback

      try {
        const userLocation = await this.getUserLocation();
        defaultLocation = userLocation;
      } catch (error) {
        console.log('Could not get user location for map default, using Dubai coordinates:', error);
      }

      if (!this.mapInstance) {
        this.mapInstance = new google.maps.Map(mapElementRef!.nativeElement, {
          center: defaultLocation,
          zoom: 13,
          mapId: environment.GOOGLE_MAPS_MAP_ID,
        } as any);
      }
    }
  }

  closePlaceDailog() {
    this.isLocationSelected = false;
  }

  /**
   * Update place details and initialize map with selected location
   * Called when a place is selected from autocomplete
   */
  updatePlaceDetails(place: any) {
    console.log('Updating place details:', place);
    this.currentPlaceDetails = place;
    if (this.showMap() && this.mapElementRef()?.nativeElement) {
      this.initGoogleMaps();
    }
  }

  async onPlaceSelect(event: any) {
    this.ngZone.run(async () => {
      try {
        const placePrediction = event?.placePrediction;

        if (!placePrediction) {
          return;
        }

        const place = placePrediction.toPlace();

        await place.fetchFields({
          fields: [
            'displayName',
            'formattedAddress',
            'location',
            'addressComponents',
            'id'
          ]
        });

        const legacyPlace = {
          place_id: place.id,
          name: place.displayName,
          formatted_address: place.formattedAddress,
          address_components: place.addressComponents?.map((component: any) => ({
            long_name: component.longText,
            short_name: component.shortText,
            types: component.types
          })) || [],
          geometry: {
            location: {
              lat: typeof place.location?.lat === 'function'
                ? place.location.lat
                : () => place.location?.lat || 0,
              lng: typeof place.location?.lng === 'function'
                ? place.location.lng
                : () => place.location?.lng || 0
            }
          }
        };

        const competitorName = this.competitorName();
        if (competitorName && competitorName.setValue) {
          competitorName.setValue(place.formattedAddress || place.displayName || '');
        }

        this.fetchPlaceInfo.emit(legacyPlace);
        this.changeDetectorRef.detectChanges();
      } catch (error) {
        console.error('Error handling place selection:', error);
      }
    });
  }

  async onAutoDetectLocation(): Promise<void> {
    if (this.isDetectingLocation) return;

    this.isDetectingLocation = true;
    this.changeDetectorRef.detectChanges();

    try {
      const location = await this.getUserLocation();

      if (this.showMap() && this.mapInstance) {
        // Center map on detected location and add marker
        this.mapInstance.setCenter(location);
        this.mapInstance.setZoom(15);

        const userLocationIcon = document.createElement('img');
        userLocationIcon.src = 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';
        userLocationIcon.style.width = '32px';
        userLocationIcon.style.height = '32px';

        new (google.maps as any).marker.AdvancedMarkerElement({
          map: this.mapInstance,
          position: location,
          title: 'Your Location',
          content: userLocationIcon
        });

        // Emit the location for parent component to handle
        this.fetchPlaceInfo.emit({
          manual_location: true,
          geometry: {
            location: {
              lat: () => location.lat,
              lng: () => location.lng
            }
          },
          formatted_address: `Detected Location (${location.lat.toFixed(6)}, ${location.lng.toFixed(6)})`,
          name: 'Detected Location'
        });
      } else {
        // In autocomplete mode, emit the location for the parent to handle
        this.fetchPlaceInfo.emit({
          manual_location: true,
          geometry: {
            location: {
              lat: () => location.lat,
              lng: () => location.lng
            }
          },
          formatted_address: `Detected Location (${location.lat.toFixed(6)}, ${location.lng.toFixed(6)})`,
          name: 'Detected Location'
        });
      }
    } catch (error) {
      console.error('Failed to detect location:', error);
      // Could show a toast message here if needed
    } finally {
      this.isDetectingLocation = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  isScriptLoaded(): boolean {
    return Array.from(document.getElementsByTagName('script')).some(
      (script) => script.id === 'google-maps-api'
    );
  }
}